import * as React from 'react';
import log from 'loglevel';
import { closeIcon, trashIcon } from '../../../ui-icons/navbar-icons';
import Emitter from './emitter';
import styles from './update-user-dialog-component.module.scss';
import { IUser } from '../../../domain/portal-domain';
import { ButtonPrimary } from '../../containers/buttons/primary-button';
import { ButtonColor, ButtonSecondary } from '../../containers/buttons/secondary-button';
import { Textbox } from '../../containers/textbox/textbox';
import { CloseButton } from '../../containers/buttons/close-button';

const ImagePreviewModal = ({ imageUrl, onClose }: { imageUrl: string; onClose: () => void }) => (
    <div className={styles.imagePreviewModal}>
        <div className={styles.modalContent}>
            <div className={styles.headerPreview}>
                <div className={styles.titlePreview}>Preview signature</div>

                <div className={styles.closeButton}>
                    <CloseButton label="Close" onClick={onClose} />
                </div>
            </div>
            <img src={imageUrl} alt="Preview" className={styles.imagePreview} />
            <div className={styles.footerPreview}>
                <ButtonSecondary label="Close" onClick={onClose} color={ButtonColor.White} />
            </div>
        </div>
    </div>
);

export interface UpdateUserDialogProps {
    user?: IUser;
    handleUploadSignature?: (userId: number, fileUploaded: File) => Promise<any>;
    removeSignatureForUser?: (userId: number) => Promise<any>;
}

export interface UpdateUserDialogState {
    modalIsOpen: boolean;
    selectedFile: File | null;
    imageUrl: string | null;
    signatureFileName: string | null;
    isSignatureRemoved: boolean;
    isImagePreviewOpen: boolean;
    errorMessage: string;
}

class UpdateUserDialog extends React.Component<UpdateUserDialogProps, UpdateUserDialogState> {
    private fileUpload: React.RefObject<HTMLInputElement>;

    constructor(props: UpdateUserDialogProps) {
        super(props);
        this.fileUpload = React.createRef();

        this.state = {
            modalIsOpen: false,
            selectedFile: null,
            imageUrl: props.user?.signatureFileUrl || null,
            signatureFileName: props.user?.signatureFileName || '',
            isSignatureRemoved: false,
            isImagePreviewOpen: false,
            errorMessage: '',
        };
    }

    componentDidMount() {
        if (this.props.user) {
            this.setState({
                imageUrl: this.props.user.signatureFileUrl || null,
                signatureFileName: this.props.user.signatureFileName || '',
            });
        }
        Emitter.on('CALL_TO_OPEN_USER_DIALOG', this.openModal);
    }

    componentDidUpdate(prevProps: UpdateUserDialogProps) {
        if (this.props.user && this.props.user.signatureFileUrl !== prevProps.user?.signatureFileUrl) {
            this.setState({
                imageUrl: this.props.user.signatureFileUrl || null,
                signatureFileName: this.props.user.signatureFileName || '',
            });
        }
    }

    componentWillUnmount() {
        Emitter.off('CALL_TO_OPEN_USER_DIALOG', this.openModal);
    }

    openModal = () => {
        this.setState({ modalIsOpen: true });
    };

    closeModal = () => {
        this.setState({
            modalIsOpen: false,
            selectedFile: null,
            imageUrl: this.props.user?.signatureFileUrl || null,
            signatureFileName: this.props.user?.signatureFileName || '',
            isSignatureRemoved: false,
            errorMessage: '',
            isImagePreviewOpen: false,
        });
    };

    handleRemoveSignature = () => {
        this.fileUpload.current.value = '';
        this.setState({ selectedFile: null, imageUrl: null, signatureFileName: null, isSignatureRemoved: true });
    };

    handleFileChange = () => {
        const file = this.fileUpload.current?.files?.[0];
        if (file) {
            if (file.type !== 'image/png' || file.size > 1024 * 1024) {
                this.setState({
                    errorMessage:
                        'Sorry - the file you selected does not seem to be a valid image file  (.png, max 1MB). Please choose another file to upload.',
                });
                return;
            }

            const imageUrl = URL.createObjectURL(file);
            this.setState({
                selectedFile: file,
                imageUrl,
                signatureFileName: file.name,
                isSignatureRemoved: false,
                isImagePreviewOpen: false,
                errorMessage: '',
            });
        }
    };

    handleSave = () => {
        const { selectedFile, isSignatureRemoved } = this.state;
        const { user } = this.props;

        if (isSignatureRemoved && this.props.removeSignatureForUser && user) {
            this.props.removeSignatureForUser(user.id).then(() => {
                this.setState({ modalIsOpen: false });
            });
        }
        if (selectedFile && this.props.handleUploadSignature && user) {
            this.props
                .handleUploadSignature(user.id, selectedFile)
                .then(() => {
                    this.setState({ modalIsOpen: false });
                })
                .catch((e) => {
                    log.error('Error during upload:', e);
                });
        }
    };

    openImagePreview = () => {
        this.setState({ isImagePreviewOpen: true });
    };

    closeImagePreview = () => {
        this.setState({ isImagePreviewOpen: false });
    };

    render() {
        const { user } = this.props;
        const { selectedFile, imageUrl, signatureFileName, isImagePreviewOpen } = this.state;
        if (!user) {
            return null;
        }
        return (
            <>
                {this.state.modalIsOpen && (
                    <>
                        <div className={styles.overlay} />
                        {isImagePreviewOpen && imageUrl && <ImagePreviewModal imageUrl={imageUrl} onClose={this.closeImagePreview} />}
                        <div className={styles.dialog}>
                            <div className={styles.dialogContent}>
                                <div className={styles.header}>
                                    <p className={styles.title}>Personal information</p>
                                    <p className={styles.icon} onClick={this.closeModal}>
                                        {closeIcon}
                                    </p>
                                </div>

                                <div className={styles.textboxes}>
                                    <Textbox label="First name" value={user?.firstName || ''} />
                                    <Textbox label="Last name" value={user?.lastName || ''} />
                                    <Textbox label="Phone number" value={user?.phoneNumber || ''} />
                                    <Textbox label="Work title" value={user?.workTitle || ''} />
                                </div>

                                <div className={styles.uploadButton}>
                                    <ButtonSecondary label="Upload signature file" onClick={() => this.fileUpload.current?.click()} />
                                </div>
                                <input
                                    type="file"
                                    accept="image/*"
                                    name="file"
                                    ref={this.fileUpload}
                                    style={{ display: 'none' }}
                                    onChange={this.handleFileChange}
                                />
                                {this.state.errorMessage !== '' ? (
                                    <p className={styles.errorMessage}>{this.state.errorMessage}</p>
                                ) : selectedFile ? (
                                    <div className={styles.filePreview} onClick={this.openImagePreview}>
                                        <div className={styles.fileText}>
                                            <span className={styles.fileName}>{selectedFile.name}</span>
                                            {' is uploaded'}
                                        </div>
                                        <span className={styles.removeIcon} onClick={this.handleRemoveSignature}>
                                            {trashIcon}
                                        </span>
                                    </div>
                                ) : signatureFileName ? (
                                    <div className={styles.filePreview} onClick={this.openImagePreview}>
                                        <div className={styles.fileText}>
                                            <span className={styles.fileName}>{signatureFileName} </span> {' is uploaded'}
                                        </div>
                                        <span className={styles.removeIcon} onClick={this.handleRemoveSignature}>
                                            {trashIcon}
                                        </span>
                                    </div>
                                ) : null}
                            </div>
                            <div className={styles.footer}>
                                <ButtonSecondary label="Cancel" onClick={this.closeModal} color={ButtonColor.Gray} />
                                <ButtonPrimary
                                    label="Save changes"
                                    disabled={
                                        (!this.state.selectedFile && !this.state.isSignatureRemoved) || this.state.errorMessage !== ''
                                    }
                                    onClick={this.handleSave}
                                />
                            </div>
                        </div>
                    </>
                )}
            </>
        );
    }
}
export default UpdateUserDialog;
